import { Dimensions, Keyboard } from 'react-native';
import { HttpStatusCode } from 'axios';
import {
  createCreateUseModal,
  Flex,
  InputBox,
  PrimaryButton,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { ApiError } from 'shared/src/api/httpClient';
import { bridge, isWeb } from 'shared/src/utils';
import { useValidatedInput } from '../../EmailLogin/hooks/useValidatedInput';
import { useLogClickChangePassword } from '../hooks/useLogClickChangePassword';
import { usePostResetPassword } from '../queries/usePostResetPassword';
import { validateNewPassword } from '../validation';

type ResetPasswordParam = {
  accessCode: string;
};

type ResetPasswordResult = {
  /** access_code 만료(서버 404) 여부 — 부모가 안내 팝업 + 화면 리셋을 처리 */
  expired: boolean;
};

const ResetPasswordContent = ({
  confirm,
  param,
}: {
  confirm: (result: ResetPasswordResult) => void;
  cancel: () => void;
  param: ResetPasswordParam;
}) => {
  const { width } = useScreenSize();
  const { mutateAsync: resetPassword, isPending: isResetting } =
    usePostResetPassword();

  const logClickChangePassword = useLogClickChangePassword();

  const passwordField = useValidatedInput(validateNewPassword);
  const passwordConfirmField = useValidatedInput(validateNewPassword);

  const isPasswordSame = passwordField.value === passwordConfirmField.value;
  const canReset =
    passwordField.isValid &&
    passwordConfirmField.value.length > 0 &&
    isPasswordSame;
  const passwordConfirmHelperText =
    passwordConfirmField.value.length > 0 && !isPasswordSame
      ? '비밀번호가 일치하지 않아요'
      : passwordConfirmField.helperText;

  const handleResetPassword = async () => {
    if (!canReset || isResetting) return;
    logClickChangePassword();
    try {
      await resetPassword({
        access_code: param.accessCode,
        new_password: passwordField.value,
      });
      confirm({ expired: false });
    } catch (error) {
      // access_code 만료(서버 404, 존재하지 않는 접근코드)는 부모가 안내 팝업 + 화면
      // 리셋을 처리하도록 결과로 넘긴다. reset_password의 404는 이 케이스 전용.
      if (error instanceof ApiError && error.status === HttpStatusCode.NotFound) {
        confirm({ expired: true });
        return;
      }
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '비밀번호 변경에 실패했어요',
      );
    }
  };

  return (
    <Flex.Column
      width={'auto'}
      borderRadius={12}
      backgroundColor={'content_always_white'}
      padding={{ horizontal: 16, vertical: 16 }}
      gap={20}
    >
      <Flex gap={16} width={width * 0.8}>
        <Typography type={'subtitle3'} color={'content_primary'}>
          {'비밀번호를 재설정해 주세요'}
        </Typography>
        <InputBox
          type={'password'}
          placeholder={'새 비밀번호 (8~16자)'}
          value={passwordField.value}
          onChangeText={passwordField.onChangeText}
          textContentType={'newPassword'}
          helperText={passwordField.helperText}
        />
        <InputBox
          type={'password'}
          placeholder={'새 비밀번호 확인'}
          value={passwordConfirmField.value}
          onChangeText={passwordConfirmField.onChangeText}
          textContentType={'newPassword'}
          helperText={passwordConfirmHelperText}
        />
      </Flex>
      <PrimaryButton
        variant={'fill'}
        color={'gray'}
        size={'large'}
        state={isResetting ? 'loading' : canReset ? undefined : 'disabled'}
        onPress={handleResetPassword}
      >
        {'변경'}
      </PrimaryButton>
    </Flex.Column>
  );
};

export const useResetPasswordModal = createCreateUseModal({
  modalProps: {
    coverScreen: isWeb,
    style: {
      margin: 0,
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    deviceWidth: isWeb ? Dimensions.get('window').width : undefined,
    animationIn: 'zoomIn',
    animationOut: 'zoomOut',
    onBackButtonPress() {
      Keyboard?.dismiss();
    },
    onBackdropPress() {
      Keyboard?.dismiss();
    },
  },
  cancelOnBackButtonPress: false,
  cancelOnBackdropPress: false,
})<ResetPasswordResult, ResetPasswordParam>(ResetPasswordContent);
